import "@/App.css";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import RepairOrders from "@/pages/RepairOrders";
import MyRepairs from "@/pages/MyRepairs";
import RepairForm from "@/pages/RepairForm";
import Inventory from "@/pages/Inventory";
import Quotes from "@/pages/Quotes";
import Billing from "@/pages/Billing";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import CrossSellSettings from "@/pages/CrossSellSettings";
import Design from "@/pages/Design";
import TimeClock from "@/pages/TimeClock";
import Reports from "@/pages/Reports";
import Expenses from "@/pages/Expenses";
import CalendarView from "@/pages/CalendarView";
import Planner from "@/pages/Planner";
import TodoList from "@/pages/TodoList";
import EmployeePortal from "@/pages/EmployeePortal";
import SignPublic from "@/pages/SignPublic";
import QuotePublic from "@/pages/QuotePublic";
import B2BPublic from "@/pages/B2BPublic";
import Recogida from "@/pages/Recogida";
import B2BAdmin from "@/pages/B2BAdmin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancelled from "@/pages/PaymentCancelled";
import ClockInPrompt from "@/components/ClockInPrompt";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-[#525252]">
        Cargando...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Si tiene must_change_password forzar a /change-password
  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

function RoleGate({ roles, children }) {
  const { hasRole } = useAuth();
  if (!hasRole(...roles)) {
    return (
      <div className="p-12 text-center">
        <div className="font-mono text-[11px] uppercase tracking-widest text-red-700">// Acceso denegado</div>
        <div className="font-display text-2xl font-black mt-2">No tienes permisos para ver esta sección.</div>
        <div className="text-sm text-[#525252] mt-2">Pide acceso al administrador del sistema.</div>
      </div>
    );
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        {/* Iter 174 — avisos globales centrados arriba (no chocan con los controles de la derecha) */}
        <Toaster position="top-center" offset={24} mobileOffset={12} />
        <RouterProvider router={router} />
      </AuthProvider>
    </div>
  );
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<Outlet />}>
      <Route path="/login" element={<Login />} />
      <Route path="/sign/:token" element={<SignPublic />} />
      {/* Iter 152 — Presupuesto público (Add-ons Fase 2, sin auth) */}
      <Route path="/presupuesto/:token" element={<QuotePublic />} />
      {/* Iter 186 — Portal público B2B (alta de empresas, sin auth) */}
      <Route path="/b2b" element={<B2BPublic />} />
      <Route path="/recogida" element={<Recogida />} />
      {/* Iter 135 — Páginas públicas de retorno PayPal (sin auth) */}
      <Route path="/pago/ok" element={<PaymentSuccess />} />
      <Route path="/pago/cancelado" element={<PaymentCancelled />} />
      <Route
        path="/change-password"
        element={
          <Protected>
            <ChangePassword />
          </Protected>
        }
      />
      <Route
        element={
          <Protected>
            <>
              <ClockInPrompt />
              <Layout />
            </>
          </Protected>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/customers/:id" element={<CustomerDetail />} />
        <Route path="/repairs" element={<RepairOrders />} />
        <Route path="/repairs/new" element={<RepairForm mode="new" />} />
        <Route path="/repairs/:id" element={<RepairForm mode="edit" />} />
        <Route path="/my-repairs" element={<MyRepairs />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/quotes" element={<Quotes />} />
        <Route path="/billing" element={<RoleGate roles={["admin", "billing", "read_only"]}><Billing /></RoleGate>} />
        <Route path="/billing/expenses" element={<RoleGate roles={["admin", "billing"]}><Expenses /></RoleGate>} />
        <Route path="/reports" element={<RoleGate roles={["admin"]}><Reports /></RoleGate>} />
        <Route path="/calendar" element={<CalendarView />} />
        <Route path="/planner" element={<Planner />} />
        <Route path="/todolist" element={<TodoList />} />
        <Route path="/portal" element={<EmployeePortal />} />
        <Route path="/time-clock" element={<TimeClock />} />
        <Route path="/b2b-admin" element={<RoleGate roles={["admin", "reception", "billing"]}><B2BAdmin /></RoleGate>} />
        <Route path="/users" element={<RoleGate roles={["admin"]}><Users /></RoleGate>} />
        <Route path="/design" element={<Navigate to="/settings?tab=design" replace />} />
        <Route path="/settings" element={<RoleGate roles={["admin"]}><Settings /></RoleGate>} />
        <Route path="/settings/cross-sell" element={<RoleGate roles={["admin"]}><CrossSellSettings /></RoleGate>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>,
  ),
);

export default App;
